import React, { useState } from 'react'
import { Link } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { CircularProgress } from '@mui/material'
import Pagination from '../Paginations'
import axios from 'axios'
import { uri } from '../../constants'

const Claims = () => {
    const uid = localStorage.getItem('uid')
    const queryClient = useQueryClient()
    const [currentPage, setCurrentPage] = useState(1)
    const [sortOrder, setSortOrder] = useState("asc")
    const [searchTerm, setSearchTerm] = useState("")

    // Fetch Claims Query
    const { 
        data: claims = [], 
        isLoading,
        error,
    } = useQuery({
        queryKey: ['claims', uid],
        queryFn: async () => {
            const response = await axios.get(`${uri}/claims/${uid}/claims`)
            return response.data
        }
    })

    // Update Claim Status Mutation
    const updateClaimMutation = useMutation({
        mutationFn: async ({ claimId, action }) => {
            const response = await axios.patch(`${uri}/claims/${uid}/claims/${claimId}`, {
                status: action
            })
            return response.data
        },
        onSuccess: () => {
            queryClient.invalidateQueries(['claims', uid])
        },
        onError: (error) => {
            console.error('Failed to update claim:', error)
        }
    })

    // Filter claims based on search term
    const filteredClaims = claims.filter((claim) => {
        if (!searchTerm) return true
        const { track, artist, channel } = claim
        const lowerCaseTerm = searchTerm.toLowerCase()
        return (
            track.toLowerCase().includes(lowerCaseTerm) ||
            artist.toLowerCase().includes(lowerCaseTerm) ||
            channel.toLowerCase().includes(lowerCaseTerm)
        )
    })

    // Sort claims
    const sortedClaims = [...filteredClaims].sort((a, b) => {
        if (sortOrder === "asc") {
            return a.track.localeCompare(b.track)
        } else {
            return b.track.localeCompare(a.track)
        }
    })

    // Pagination
    const itemsPerPage = 50
    const indexOfLastItem = currentPage * itemsPerPage
    const indexOfFirstItem = indexOfLastItem - itemsPerPage
    const currentItems = sortedClaims.slice(indexOfFirstItem, indexOfLastItem)
    const totalPages = Math.ceil(sortedClaims.length / itemsPerPage)

    const handleSort = (property) => {
        setCurrentPage(1)
        setSortOrder(sortOrder === "asc" ? "desc" : "asc")
    }

    const handleSearch = (event) => {
        setSearchTerm(event.target.value)
        setCurrentPage(1)
    }

    return (
        <div className='dash-section'>
            <h1 className='dash-title'>Claims</h1>
            <div className="row">
                <div className="col-12 mt-4">
                    <div className="dash-card">
                        <div className="dash-card-heading mb-2">
                            <h2 className="dash-card-title">Claims Report</h2>
                        </div>
                        
                        <input 
                            type="text" 
                            className='dash-input' 
                            onChange={handleSearch} 
                            placeholder='Search...' 
                        />

                        {/* Loading State */}
                        {isLoading && (
                            <div className="text-center py-5">
                                <CircularProgress />
                            </div>
                        )}

                        {/* Error State */}
                        {error && (
                            <div className="alert alert-danger mt-3">
                                Failed to load claims. Please try again later.
                            </div>
                        )}

                        {/* Empty State */}
                        {!isLoading && !error && claims.length === 0 && (
                            <div className="text-center py-5">
                                <h5>No claims found</h5>
                                <p>There are currently no claims to display</p>
                            </div>
                        )}

                        {/* Data Table */}
                        {!isLoading && !error && claims.length > 0 && (
                            <div className="table-responsive mt-3">
                                <table className="table dash-table">
                                    <thead>
                                        <tr>
                                            <td>
                                                <div className='table-sort' onClick={() => handleSort('track')}>
                                                    Track
                                                    {sortOrder === "asc" 
                                                        ? <i className="fi fi-ss-angle-small-down"></i>
                                                        : <i className="fi fi-ss-angle-small-up"></i>
                                                    }
                                                </div>
                                            </td>
                                            <td>Artist</td>
                                            <td>Channel Claimed</td>
                                            <td>Claim URL</td>
                                            <td>Views</td>
                                            <td>Action</td>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {currentItems.map((claim, i) => (
                                            <tr key={i}>
                                                <td>
                                                    <div className="table-item">
                                                        {claim.track}
                                                    </div>
                                                </td>
                                                <td>
                                                    <div className="table-item">
                                                        {claim.artist}
                                                    </div>
                                                </td>
                                                <td>
                                                    <div className="table-item">
                                                        {claim.channel}
                                                    </div>
                                                </td>
                                                <td>
                                                    <div className="table-item">
                                                        https://youtu.be/{claim.url}
                                                    </div>
                                                </td>
                                                <td>
                                                    <div className="table-item">
                                                        {claim.views}
                                                    </div>
                                                </td>
                                                <td>
                                                    <div className="table-item">
                                                        <button
                                                            className="btn btn-link p-0"
                                                            onClick={() => updateClaimMutation.mutate({
                                                                claimId: claim.id,
                                                                action: 'release'
                                                            })}
                                                            disabled={updateClaimMutation.isLoading}
                                                        >
                                                            Release
                                                        </button>
                                                        {' / '}
                                                        <button
                                                            className="btn btn-link p-0 text-danger"
                                                            onClick={() => updateClaimMutation.mutate({
                                                                claimId: claim.id,
                                                                action: 'takedown'
                                                            })}
                                                            disabled={updateClaimMutation.isLoading}
                                                        >
                                                            Takedown
                                                        </button>
                                                    </div>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}

                        {/* Pagination */}
                        {!isLoading && !error && claims.length > 0 && (
                            <Pagination 
                                currentPage={currentPage} 
                                totalPages={totalPages} 
                                handlePageChange={setCurrentPage} 
                            />
                        )}
                    </div>
                </div>
            </div>

            {/* Loading Overlay for Mutations */}
            {updateClaimMutation.isLoading && (
                <div 
                    style={{
                        position: 'fixed',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        backgroundColor: 'rgba(0,0,0,0.4)',
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        zIndex: 9999
                    }}
                >
                    <CircularProgress />
                </div>
            )}
        </div>
    )
}

export default Claims